import {
  type GapQuestion,
  type GapQuestionEditableFields,
  type GapQuestionResolutionStatus,
  type GapQuestionThreadAuthor,
  type GapQuestionThreadEntry,
  type GapQuestionThreadEntryType,
  type PatientGapAnalysis,
  type SharedGapQuestionRecord,
} from './types';

const GAP_ANALYSIS_STORAGE_KEY = 'patient_gap_analyses_v1';
export const GAP_ANALYSIS_UPDATED_EVENT = 'patient-gap-analysis-updated';

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const persistGapAnalyses = (analyses: PatientGapAnalysis[]): void => {
  localStorage.setItem(GAP_ANALYSIS_STORAGE_KEY, JSON.stringify(analyses));

  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(GAP_ANALYSIS_UPDATED_EVENT));
  }
};

const sortThread = (thread: GapQuestionThreadEntry[]): GapQuestionThreadEntry[] =>
  [...thread].sort(
    (left, right) => new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime()
  );

const buildThreadEntry = (
  author: GapQuestionThreadAuthor,
  entryType: GapQuestionThreadEntryType,
  content: string,
  createdAt: string
): GapQuestionThreadEntry => ({
  id: `gap-thread-${genId()}`,
  author,
  entryType,
  content,
  createdAt,
});

const isQuestionArchived = (question: GapQuestion): boolean =>
  question.patientStatus === 'ANSWERED' || Boolean(question.patientRespondedAt);

const normalizeQuestion = (
  question: GapQuestion,
  analysisCreatedDate: string
): GapQuestion => {
  const thread = Array.isArray(question.thread) ? [...question.thread] : [];

  if (question.sharedAt && !thread.some((entry) => entry.entryType === 'SHARED')) {
    thread.push(
      buildThreadEntry('SYSTEM', 'SHARED', 'Question shared with patient.', question.sharedAt)
    );
  }

  if (
    question.patientResponse &&
    question.patientRespondedAt &&
    !thread.some(
      (entry) =>
        entry.entryType === 'PATIENT_RESPONSE' &&
        entry.content === question.patientResponse &&
        entry.createdAt === question.patientRespondedAt
    )
  ) {
    thread.push(
      buildThreadEntry(
        'PATIENT',
        'PATIENT_RESPONSE',
        question.patientResponse,
        question.patientRespondedAt
      )
    );
  }

  const sortedThread = sortThread(thread);
  const latestPatientResponse = [...sortedThread]
    .reverse()
    .find((entry) => entry.entryType === 'PATIENT_RESPONSE');
  const lastUpdatedAt =
    sortedThread[sortedThread.length - 1]?.createdAt ??
    question.resolutionUpdatedAt ??
    question.sharedAt ??
    analysisCreatedDate;

  return {
    ...question,
    thread: sortedThread,
    patientResponse: latestPatientResponse?.content,
    patientRespondedAt: latestPatientResponse?.createdAt,
    patientStatus: latestPatientResponse
      ? 'ANSWERED'
      : question.sharedWithPatient
        ? 'PENDING_PATIENT'
        : 'UNSENT',
    lastUpdatedAt,
    resolutionStatus: question.resolutionStatus ?? 'OPEN',
  };
};

const normalizeAnalysis = (analysis: PatientGapAnalysis): PatientGapAnalysis => ({
  ...analysis,
  questions: analysis.questions.map((question) => normalizeQuestion(question, analysis.createdDate)),
});

const isAnalysisArray = (value: unknown): value is PatientGapAnalysis[] => Array.isArray(value);

export const loadGapAnalyses = (): PatientGapAnalysis[] => {
  try {
    const raw = localStorage.getItem(GAP_ANALYSIS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return isAnalysisArray(parsed) ? parsed.map(normalizeAnalysis) : [];
  } catch {
    return [];
  }
};

export const saveGapAnalysis = (analysis: PatientGapAnalysis): void => {
  const existing = loadGapAnalyses();
  persistGapAnalyses([normalizeAnalysis(analysis), ...existing]);
};

export const updateGapAnalysis = (
  id: string,
  updates: Partial<PatientGapAnalysis>
): void => {
  const existing = loadGapAnalyses();
  const updated = existing.map((analysis) =>
    analysis.id === id ? normalizeAnalysis({ ...analysis, ...updates, id: analysis.id }) : analysis
  );
  persistGapAnalyses(updated);
};

export const updateGapQuestion = (
  analysisId: string,
  questionId: string,
  updates: Partial<GapQuestion>
): void => {
  const existing = loadGapAnalyses();
  const updated = existing.map((analysis) => {
    if (analysis.id !== analysisId) {
      return analysis;
    }

    return {
      ...analysis,
      questions: analysis.questions.map((question) =>
        question.id === questionId
          ? normalizeQuestion({ ...question, ...updates, id: question.id }, analysis.createdDate)
          : question
      ),
    };
  });

  persistGapAnalyses(updated);
};

export const editGapQuestion = (
  analysisId: string,
  questionId: string,
  updates: Partial<GapQuestionEditableFields>
): void => {
  const existing = loadGapAnalyses();
  const updated = existing.map((analysis) => {
    if (analysis.id !== analysisId) {
      return analysis;
    }

    return {
      ...analysis,
      questions: analysis.questions.map((question) => {
        if (question.id !== questionId || isQuestionArchived(question)) {
          return question;
        }

        return normalizeQuestion(
          {
            ...question,
            ...updates,
          },
          analysis.createdDate
        );
      }),
    };
  });

  persistGapAnalyses(updated);
};

export const deleteGapQuestion = (analysisId: string, questionId: string): void => {
  const existing = loadGapAnalyses();
  const updated = existing.map((analysis) => {
    if (analysis.id !== analysisId) {
      return analysis;
    }

    return {
      ...analysis,
      questions: analysis.questions.filter(
        (question) => question.id !== questionId || isQuestionArchived(question)
      ),
    };
  });

  persistGapAnalyses(updated);
};

export const updateGapQuestionShare = (
  analysisId: string,
  questionId: string,
  sharedWithPatient: boolean
): void => {
  const existing = loadGapAnalyses();
  const updated = existing.map((analysis) => {
    if (analysis.id !== analysisId) {
      return analysis;
    }

    return {
      ...analysis,
      questions: analysis.questions.map((question) => {
        if (question.id !== questionId || isQuestionArchived(question)) {
          return question;
        }

        const sharedAt = sharedWithPatient
          ? question.sharedAt ?? new Date().toISOString()
          : question.sharedAt;
        const ensuredSharedAt = sharedAt ?? new Date().toISOString();
        const thread = question.thread ?? [];
        const nextThread =
          sharedWithPatient && !thread.some((entry) => entry.entryType === 'SHARED')
            ? sortThread([
                ...thread,
                buildThreadEntry('SYSTEM', 'SHARED', 'Question shared with patient.', ensuredSharedAt),
              ])
            : thread;

        return normalizeQuestion(
          {
            ...question,
            sharedWithPatient,
            sharedAt: sharedWithPatient ? ensuredSharedAt : sharedAt,
            thread: nextThread,
          },
          analysis.createdDate
        );
      }),
    };
  });

  persistGapAnalyses(updated);
};

export const answerGapQuestion = (
  analysisId: string,
  questionId: string,
  patientResponse: string
): void => {
  const respondedAt = new Date().toISOString();
  const existing = loadGapAnalyses();
  const updated = existing.map((analysis) => {
    if (analysis.id !== analysisId) {
      return analysis;
    }

    return {
      ...analysis,
      questions: analysis.questions.map((question) =>
        question.id === questionId && !isQuestionArchived(question)
          ? normalizeQuestion(
              {
                ...question,
                thread: sortThread([
                  ...(question.thread ?? []),
                  buildThreadEntry('PATIENT', 'PATIENT_RESPONSE', patientResponse, respondedAt),
                ]),
              },
              analysis.createdDate
            )
          : question
      ),
    };
  });

  persistGapAnalyses(updated);
};

export const addClinicianGapQuestionComment = (
  analysisId: string,
  questionId: string,
  comment: string
): void => {
  const createdAt = new Date().toISOString();
  const existing = loadGapAnalyses();
  const updated = existing.map((analysis) => {
    if (analysis.id !== analysisId) {
      return analysis;
    }

    return {
      ...analysis,
      questions: analysis.questions.map((question) =>
        question.id === questionId && !isQuestionArchived(question)
          ? normalizeQuestion(
              {
                ...question,
                thread: sortThread([
                  ...(question.thread ?? []),
                  buildThreadEntry('CLINICIAN', 'CLINICIAN_COMMENT', comment, createdAt),
                ]),
              },
              analysis.createdDate
            )
          : question
      ),
    };
  });

  persistGapAnalyses(updated);
};

export const updateGapQuestionResolution = (
  analysisId: string,
  questionId: string,
  resolutionStatus: GapQuestionResolutionStatus
): void => {
  const resolutionUpdatedAt = new Date().toISOString();
  const existing = loadGapAnalyses();
  const updated = existing.map((analysis) => {
    if (analysis.id !== analysisId) {
      return analysis;
    }

    return {
      ...analysis,
      questions: analysis.questions.map((question) =>
        question.id === questionId
          ? normalizeQuestion(
              {
                ...question,
                resolutionStatus,
                resolutionUpdatedAt,
              },
              analysis.createdDate
            )
          : question
      ),
    };
  });

  persistGapAnalyses(updated);
};

export const loadSharedGapQuestionsForPatient = (
  patientId?: string,
  patientName?: string
): SharedGapQuestionRecord[] => {
  const normalizedName = patientName?.trim().toLowerCase();

  return loadGapAnalyses()
    .flatMap((analysis) =>
      analysis.questions
        .filter((question) => question.sharedWithPatient)
        .filter(
          () =>
            (patientId !== undefined && analysis.patientId === patientId) ||
            (normalizedName !== undefined && analysis.patientName.trim().toLowerCase() === normalizedName)
        )
        .map((question) => ({
          analysisId: analysis.id,
          analysisCreatedDate: analysis.createdDate,
          patientId: analysis.patientId,
          patientName: analysis.patientName,
          question,
        }))
    )
    .sort(
      (left, right) =>
        new Date(right.question.sharedAt ?? right.analysisCreatedDate).getTime() -
        new Date(left.question.sharedAt ?? left.analysisCreatedDate).getTime()
    );
};